// ignore-string-externalization

import React from 'react';
import { ModuleType } from '../types';
import { useLocale } from '@spotify-internal/i18n-core';
import { RelatedStoriesListModule as FrodorRelatedStoriesListModule } from '@spotify-internal/frodor-modules';
import { ContentfulAsset } from '../../Contentful';
import { isContentReleased } from '../../../utils/dateUtils';

type RelatedStoryItem = {
  id: string;
  title: string;
  description?: string;
  url: string;
  image?: ContentfulAsset;
  contentType: 'articlePage' | 'landingPage' | 'videoPage';
};

type RelatedStoriesListModuleType = ModuleType & {
  headline?: string;
  items?: any[]; // Raw items from Contentful, not SearchHit
};

// Local mapping function that only supports the three required content types
const itemToRelatedStoryItem = (
  item: any,
  locale: string = 'en',
): RelatedStoryItem | null => {
  if (item.isSearchHit) {
    return item;
  }

  if (locale !== 'en' && !isItemValidForLocale(item, locale)) {
    return null;
  }

  const ctype = item.contentType as string;
  if (
    !ctype ||
    !item.id ||
    !(item.urlSlug || item.url) ||
    !isContentReleased(item.releaseDate)
  ) {
    return null;
  }

  // Only allow the three supported content types
  if (!['articlePage', 'landingPage', 'videoPage'].includes(ctype)) {
    return null;
  }

  const doc: RelatedStoryItem = {
    contentType: ctype as 'articlePage' | 'landingPage' | 'videoPage',
    id: item.id,
    title: '',
    url: '',
  };

  // Map fields based on content type
  switch (ctype) {
    case 'landingPage':
    case 'videoPage':
    case 'articlePage':
      doc.title = item.title?.trim() || '';
      doc.description = item.description?.trim();
      doc.image =
        item.socialCard ||
        item.image ||
        item.thumbnailImage ||
        item.headerModule?.image;
      break;
    default:
      return null;
  }

  doc.url = getLinkForRelatedStoryItem(item);
  return doc.title ? doc : null;
};

// Helper functions from the original itemToSearchHit
const isItemValidForLocale = (item: any, locale: string) => {
  return (
    item.contentType === 'customGridContent' ||
    item.translated === true ||
    itemTranslatedForLanguage(item, locale)
  );
};

const itemTranslatedForLanguage = (item: any, locale: string) => {
  return item.languages?.split(',').indexOf(getContentfulLocale(locale)) >= 0;
};

const getContentfulLocale = (locale: string) => {
  // This is a simplified version - you might need to import the actual function
  return locale === 'en' ? 'en-US' : locale;
};

const getLinkForRelatedStoryItem = (doc: any): string => {
  const ctype = doc.contentType;
  if (ctype === 'videoPage') {
    return `/video/${doc.urlSlug}`;
  }
  if (ctype === 'landingPage') {
    return `/${doc.urlSlug}`;
  }
  if (ctype === 'articlePage') {
    return `/blog/${doc.urlSlug}`;
  }
  return '/';
};

export const RelatedStoriesListModule: React.FC<
  RelatedStoriesListModuleType
> = ({ headline = '', items = [] }) => {
  const locale = useLocale();
  const relatedStories = (
    items.map(item =>
      itemToRelatedStoryItem(item, locale),
    ) as NonNullable<RelatedStoryItem>[]
  ).filter(Boolean);
  return (
    <FrodorRelatedStoriesListModule
      contentType="relatedStoriesListModule"
      headline={headline}
      items={relatedStories}
    />
  );
};
